// aluno/home.js — portal do aluno (tela principal pós-login).
//
// Origem (monólito): linhas 4718-5091 (HTML portalScreen),
//   5454-5470 (showPortal), 6993-7020 (applyAlunoPortal).
//
// Dependências:
//   - auth   (core/auth.js)  — auth.getCurrent(), auth.primeiroNome(), auth.logout()
//   - router (core/router.js)— router.go() para navegação por tiles
//
// Exporta:
//   function render(container) — monta o portal do aluno no elemento recebido.
//
// Notas v19:
//   - Inline onclick removidos; eventos via addEventListener.
//   - Painel admin e carrossel de cursos preservados mas sem lógica de toggle
//     (o painel admin nesta versão abre apenas para turma 'Admin').
//   - Tiles navegam via router.go() em vez de showX().
//   - R3 (v19.0.1): cards do carrossel agora abrem o bottom-sheet de detalhe
//     do curso via cursoDetail.open(id) — restauração registrada em
//     docs/auditoria-regressoes-v19.md e docs/removidos-sem-autorizacao-v19.md.

import { auth }        from '../core/auth.js';
import { router }      from '../core/router.js';
import { cursoDetail } from './curso-detail.js';

// ── HTML dos tiles de acesso rápido ─────────────────────────────────────────

/**
 * Gera um tile de navegação.
 * @param {string} id       - identificador para seleção via querySelector
 * @param {string} icone    - emoji
 * @param {string} rotulo   - texto principal
 * @param {string} sub      - texto secundário
 * @param {string} [href]   - URL externa (opcional; se presente, abre nova aba)
 */
function _tile(id, icone, rotulo, sub, href) {
  if (href) {
    return `
      <a class="portal-tile portal-tile-wide" href="${href}" target="_blank" rel="noopener" data-tile="${id}">
        <div class="portal-tile-icon">${icone}</div>
        <div>
          <div class="portal-tile-label">${rotulo}</div>
          <div class="portal-tile-sub">${sub}</div>
        </div>
        <div class="portal-tile-arrow">›</div>
      </a>`;
  }
  return `
    <button class="portal-tile portal-tile-wide" type="button" data-tile="${id}">
      <div class="portal-tile-icon">${icone}</div>
      <div>
        <div class="portal-tile-label">${rotulo}</div>
        <div class="portal-tile-sub">${sub}</div>
      </div>
      <div class="portal-tile-arrow">›</div>
    </button>`;
}

// ── Cards de cursos (carrossel) ──────────────────────────────────────────────

const CURSOS_HTML = `
  <div class="curso-card" data-curso="oratoria-power" role="button" tabindex="0"
       style="background:linear-gradient(145deg,#6A001F 0%,#B3002D 100%);cursor:pointer;">
    <span class="curso-card-novo">✦ NOVO</span>
    <div class="curso-card-icon">⚡</div>
    <div class="curso-card-title">Oratória Power</div>
    <div class="curso-card-desc">Domine palcos, câmeras e grandes audiências com autoridade e presença.</div>
    <span class="curso-card-cta">Ver detalhes →</span>
  </div>
  <div class="curso-card" data-curso="persuasao" role="button" tabindex="0"
       style="background:linear-gradient(145deg,#1a0a3e 0%,#4a1080 100%);cursor:pointer;">
    <span class="curso-card-novo">✦ NOVO</span>
    <div class="curso-card-icon">🧠</div>
    <div class="curso-card-title">Técnicas de Persuasão</div>
    <div class="curso-card-desc">Influencie decisões, conquiste sim's e lidere conversas com estratégia.</div>
    <span class="curso-card-cta">Ver detalhes →</span>
  </div>
  <div class="curso-card" data-curso="corporativo" role="button" tabindex="0"
       style="background:linear-gradient(145deg,#0a2a1a 0%,#0e5c35 100%);cursor:pointer;">
    <div class="curso-card-icon">🏢</div>
    <div class="curso-card-title">Cursos Corporativos</div>
    <div class="curso-card-desc">Treinamentos sob medida para equipes e lideranças da sua empresa.</div>
    <span class="curso-card-cta">Ver detalhes →</span>
  </div>
  <div class="curso-card" data-curso="palestras" role="button" tabindex="0"
       style="background:linear-gradient(145deg,#1a1200 0%,#7a4e00 100%);cursor:pointer;">
    <div class="curso-card-icon">🎤</div>
    <div class="curso-card-title">Palestras</div>
    <div class="curso-card-desc">Conteúdo de alto impacto para eventos, congressos e convenções.</div>
    <span class="curso-card-cta">Ver detalhes →</span>
  </div>
  <div class="curso-card" style="background:linear-gradient(145deg,#111 0%,#2a2a2a 100%);opacity:0.75;cursor:default;">
    <span class="curso-card-novo" style="background:#555;color:#ccc;">EM BREVE</span>
    <div class="curso-card-icon">🚀</div>
    <div class="curso-card-title">Em breve...</div>
    <div class="curso-card-desc">Novas formações chegando. Fique de olho nas novidades da Eloquence.</div>
    <span class="curso-card-cta" style="opacity:0.4;">Aguarde</span>
  </div>
`;

// ── Função principal exportada ───────────────────────────────────────────────

/**
 * Renderiza o portal do aluno dentro de `container`.
 * Chamado pelo router para a rota #/aluno.
 *
 * @param {HTMLElement} container - elemento #app
 */
export function render(container) {
  const user    = auth.getCurrent();
  const nome    = user ? auth.primeiroNome(user) : 'Aluno';
  const turma   = user?.turma ?? '—';
  const isAdmin = turma === 'Admin';

  container.innerHTML = `
    <div id="portal-root" class="portal-root">

      <!-- Header -->
      <header class="portal-header">
        <div class="portal-header-inner">
          <button id="portal-back-btn" type="button"
            style="width:38px;height:38px;border-radius:50%;border:none;background:rgba(255,255,255,0.18);color:white;font-size:18px;cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:background 0.2s;"
            aria-label="Voltar">←</button>
          <div class="portal-brand" style="flex:1;margin-left:12px">
            <span class="portal-brand-label">Eloquence</span>
            <span class="portal-brand-title">Portal do Aluno</span>
          </div>
          <button id="portal-sair-btn" type="button" title="Sair"
            style="width:38px;height:38px;border-radius:50%;border:none;background:rgba(255,255,255,0.12);color:rgba(255,255,255,0.8);font-size:15px;cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:background 0.2s;"
            aria-label="Sair">🚪</button>
        </div>
      </header>

      <main class="portal-main">

        <!-- Saudação -->
        <div class="portal-greeting">
          <div class="portal-greeting-label">Bem-vindo</div>
          <div class="portal-greeting-title" id="portal-greeting-name">Olá, ${nome}! 👋</div>
          <div class="portal-greeting-sub" id="portal-greeting-sub">
            ${isAdmin ? 'Acesso administrativo' : `MBO Pós — Turma ${turma}`}
          </div>
        </div>

        <!-- Badge de turma (apenas para alunos) -->
        ${!isAdmin ? `
        <div class="portal-turma-badge">
          <div style="width:48px;height:48px;border-radius:12px;background:linear-gradient(135deg,#6A001F,#B3002D);display:flex;align-items:center;justify-content:center;flex-shrink:0">
            <span style="font-size:11px;font-weight:900;color:#fff;letter-spacing:0.5px">MBO</span>
          </div>
          <div style="flex:1">
            <div style="font-size:11px;font-weight:700;color:#9B9B9B;text-transform:uppercase;letter-spacing:1.5px;margin-bottom:2px">Sua Turma</div>
            <div style="font-size:18px;font-weight:900;color:#0A0A0A">Turma <span id="portal-turma-num">${turma}</span></div>
            <div style="font-size:12px;font-weight:600;color:#6A001F;margin-top:1px">MBO Pós 2026</div>
          </div>
          <div style="font-size:24px">🎓</div>
        </div>
        ` : ''}

        <!-- Acesso rápido -->
        <div class="portal-section-title">Acesso Rápido</div>
        <div class="portal-grid" id="portal-grid">
          ${_tile('frequencia', '📊', 'Minha Frequência', 'Presenças e faltas')}
          ${_tile('materiais',  '📝', 'Materiais de Aula', 'Ver matérias por professor')}
          ${_tile('secretaria', '💬', 'Fale com a Secretaria', 'Atendimento via WhatsApp',
            'https://wa.me/5571982505900?text=Ol%C3%A1%2C%20Gabriela!%20Gostaria%20de%20tirar%20uma%20d%C3%BAvida%20sobre%20o%20MBO')}
          ${_tile('kiwify', '▶️', 'Plataforma Kiwify', 'Acessar área de membros',
            'https://dashboard.kiwify.com/members-area/15a0267c-2464-468a-942f-3b26d6b876da?from=products%2Fedit%2F6624a1f0-f99a-11ed-a6c5-25a9361431e9&tab=content')}
        </div>

        <!-- Carrossel: Nossos Cursos -->
        <div class="cursos-section">
          <div class="cursos-header">
            <div>
              <div class="cursos-section-title">Nossos Cursos</div>
              <div class="cursos-section-sub">Expanda seu repertório e suas habilidades</div>
            </div>
            <div class="cursos-badge">✦ Novidades</div>
          </div>
          <div class="cursos-track" id="portal-cursos-track">
            ${CURSOS_HTML}
          </div>
          <a class="cursos-cta-banner"
            href="https://wa.me/5571982505900?text=Ol%C3%A1%2C%20gostaria%20de%20conhecer%20todos%20os%20cursos%20da%20Eloquence!"
            target="_blank" rel="noopener">
            <span class="cursos-cta-icon">💬</span>
            <div class="cursos-cta-info">
              <div class="cursos-cta-title">Fale com a Eloquence</div>
              <div class="cursos-cta-sub">Descubra o curso ideal para você</div>
            </div>
            <span class="cursos-cta-arrow">›</span>
          </a>
        </div>

      </main>
    </div>
  `;

  // ── Wiring de eventos ─────────────────────────────────────────────────────

  // Botão voltar → tela de login
  container.querySelector('#portal-back-btn').addEventListener('click', () => {
    router.go('/login');
  });

  // Botão sair → logout + login
  container.querySelector('#portal-sair-btn').addEventListener('click', () => {
    auth.logout();
    router.go('/login');
  });

  // Delegação dos tiles de navegação interna
  container.querySelector('#portal-grid').addEventListener('click', (e) => {
    const btn = e.target.closest('[data-tile]');
    if (!btn) return;
    const destino = btn.dataset.tile;
    // Tiles externos (href) já cuidam de si mesmos como <a>.
    if (btn.tagName === 'A') return;
    switch (destino) {
      case 'frequencia': router.go('/aluno/frequencia'); break;
      case 'materiais':  router.go('/aluno/materiais');  break;
      // oratória e financeiro mantidos como rotas para uso futuro
      case 'oratoria':   router.go('/aluno/oratoria');   break;
      case 'financeiro': router.go('/aluno/financeiro'); break;
    }
  });

  // R3: cards do carrossel abrem detalhe do curso (bottom-sheet)
  const cursosTrack = container.querySelector('#portal-cursos-track');
  if (cursosTrack) {
    cursosTrack.addEventListener('click', (e) => {
      const card = e.target.closest('[data-curso]');
      if (!card) return;
      cursoDetail.open(card.dataset.curso);
    });
    cursosTrack.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const card = e.target.closest('[data-curso]');
      if (!card) return;
      e.preventDefault();
      cursoDetail.open(card.dataset.curso);
    });
  }
}
